import React, { FC, MouseEvent, ReactElement, RefObject, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { Header } from "@/components/Header";
import { SubSection } from "@/components/Section";
import { ShareButton, TShareTarget } from "@/components/Share";
import { Text } from "@/components/Text";
import { TextListWithDot } from "@/components/TextListWithDot";
import { agoDate } from "@/core/dates";
import { MEDIA_SIZE_SM } from "@/core/media";
import { getJobRequestClientInfo } from "@/data/job-requests";
import { formatMultipleLocations, formatMultipleLocationsWithCounter } from "@/data/locations";
import { Flex } from "@/eworkitComponents";
import { useFormats } from "@/hooks/Formats";
import { RouteSection, usePreviousRoutePath } from "@/infrastructure/Route";
import { SessionContext } from "@/infrastructure/Session";
import { JobRequestFavouriteButton } from "@/views/components/JobRequests/Partials";
import { SkillLevel } from "@/views/components/Skills";
import { JobRequestClientAvatar } from "@/views/JobRequests/JobRequestClientAvatar/JobRequestClientAvatar";
import { router } from "@/views/router";

import { IJobRequestListRecordProps } from "./JobRequestListRenderer";

interface IJobRequestRecordProps extends IJobRequestListRecordProps {
  lastJobOfferRef?: RefObject<HTMLDivElement>;
}

export const JobRequestRecord: FC<IJobRequestRecordProps> = ({
  lastJobOfferRef,
  record,
  variant = "default",
  onUpdate,
}) => {
  const { authenticated } = useContext(SessionContext);
  const { currentLocationState } = usePreviousRoutePath();
  const { t } = useTranslation("session");
  const { formatRate } = useFormats();
  const { sendEvent } = useAnalytics();

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const routePath = authenticated ? "job-requests.detail" : "public-job-requests.detail";
  const publicUrl = window.location.origin + router.get("public-job-requests.detail", { jobRequestId: record.id }).path;

  const baseClass = "job-request-record";

  const { name } = getJobRequestClientInfo(record);

  const resultUnit = record.rate.clientRateType === "HOURLY" ? "h" : undefined;

  const renderDetails = useCallback(
    (...details: Array<ReactElement | null>) => {
      return isSmallScreen ? <Flex direction="column">{details}</Flex> : <TextListWithDot>{details}</TextListWithDot>;
    },
    [isSmallScreen],
  );

  const handleButtonsClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleShared = useCallback(
    (method: TShareTarget | "link") => {
      sendEvent({
        type: "share_job",
        id: record.id,
        name: record.title!,
        location: formatMultipleLocations(t, record.locations, record.locations?.length),
        method,
      });
    },
    [record, sendEvent, t],
  );

  return (
    <RouteSection
      flex
      borderRadius
      state={currentLocationState}
      path={router.get(routePath, { jobRequestId: record.id }).path}>
      <Flex
        border={[false, true, false, true]}
        ref={lastJobOfferRef}
        grow={0}
        direction="row"
        padding={isSmallScreen ? ["md"] : ["lg"]}
        innerMargin="md"
        align="flex-start">
        <Flex justify={isSmallScreen ? "space-between" : "center"} align="center">
          <JobRequestClientAvatar size="lg" jobRequest={record} />
        </Flex>

        <Flex
          grow={1}
          direction={isSmallScreen ? "column" : "row"}
          justify={isSmallScreen ? "flex-start" : "space-between"}
          innerMargin={isSmallScreen ? "xs" : "md"}>
          <Flex direction="column" innerMargin={!isSmallScreen && "xs"}>
            <Flex direction="row" innerMargin="sm">
              <Header
                size={isSmallScreen ? "xs" : variant === "default" ? "sm" : "md"}
                text={record.title}
                weight="bold"
                className={`${baseClass}__header`}
              />
            </Flex>

            {renderDetails(
              record.systemId ? (
                <Text key="jr-id" color="black">
                  {record.systemId}
                </Text>
              ) : null,
              <Text key="client" color="black">
                {name}
              </Text>,
              record.locations?.length ? (
                <Text key="location" color="black">
                  {formatMultipleLocationsWithCounter(t, record.locations)}
                </Text>
              ) : null,
            )}

            <Flex direction="row" innerMargin="sm">
              {!record.public && (
                <Flex padding={[isSmallScreen ? "xs" : "sm", false, false, false]}>
                  <SkillLevel textColor="success" text={t("JOB_REQUESTS.DETAIL.EXCLUSIVE")} round mono={false} />
                </Flex>
              )}
              {record.remoteness === 100 && (
                <Flex padding={[isSmallScreen ? "xs" : "sm", false, false, false]}>
                  <SkillLevel text={t("JOB_REQUESTS.DETAIL.REMOTENESS.REMOTE")} round mono={false} />
                </Flex>
              )}
              {record.rate && !!record.rate.maxRate && (
                <Flex padding={[isSmallScreen ? "xs" : "sm", false, false, false]}>
                  <SkillLevel
                    text={formatRate(record.rate.maxRate, record.rate.currency, resultUnit)}
                    round
                    mono={false}
                  />
                </Flex>
              )}
            </Flex>
          </Flex>

          {variant === "short" && (
            <Flex grow={0} direction="column" justify="flex-end">
              <Text size="md" color="grey3" whiteSpace="nowrap">
                {agoDate(record.firstDayOfApplications)}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>

      {variant === "default" && (
        <SubSection
          role="bottom"
          padding={["sm", "lg"]}
          background="background-card-bottom"
          flex
          direction="row"
          justify="space-between"
          align="center">
          <div onClick={handleButtonsClick}>
            <Flex direction="row" align="center" innerMargin="xs">
              <JobRequestFavouriteButton record={record} onUpdate={onUpdate} />
              {record.public && (
                <ShareButton url={publicUrl} icon="share-1" variant="card-bottom" showLabel onShared={handleShared} />
              )}
            </Flex>
          </div>
          <Flex align="center">
            <Text size="md" color="grey3">
              {agoDate(record.firstDayOfApplications)}
            </Text>
          </Flex>
        </SubSection>
      )}
    </RouteSection>
  );
};
